/**
 * The gameplay page (author's addition): the goal, a turn, the synergies, the invaders and the weapons. The tables are
 * built from the game's own data (INVADERS, WEAPONS, CHEST_TABLE), so that they follow its values; the texts that are
 * not in the data (what a weapon does, a death burst, a legacy) are written here. Every animation is a scene of the real
 * game (stage.ts, scenes.ts).
 */
import { MATERIALS, Physique } from '@invaders/sim';
import {
  INVADERS, WEAPONS, BAR_ORDER, CHEST_TABLE, PISTOL_SHOTS, WATER_HURT, WATER_HEAL,
  WEAPON_BAZOOKA, WEAPON_GRENADE, WEAPON_PISTOL, WEAPON_SANDBAG, WEAPON_DRILL, WEAPON_MINE, WEAPON_WAVE, WEAPON_FLAMER,
  WEAPON_COCKTAIL, WEAPON_CRYO, WEAPON_ICE_MINE, WEAPON_METEOR, WEAPON_LASER, WEAPON_GEYSER, WEAPON_DOLL, WEAPON_REMOTE,
  WEAPON_JETPACK, WEAPON_BATTERY, WEAPON_ACID_BARREL, WEAPON_POWDER_BARREL, WEAPON_VOLCANO, WEAPON_ROOMBA,
  ZORG, GOLEM, SANDMAN, FISHMAN, LAVAMAN, TREEMAN, ROBOTMAN, CLOUDMAN, FIREMAN, BARBE_NOIRE, LIZARDMAN, SNOWMAN,
  type InvaderKind,
} from '@invaders/game';
import { weaponIcon } from '../shared/weapon-icons';
import { drawFrame } from '../shared/sprites';
import { cssColor } from '../shared/palette';
import { Stage, demoPanel } from './stage';
import { GOAL_SCENES, SYNERGY_SCENES, TURN_SCENES, WEAPON_SCENES, invaderScene } from './scenes';
import type { Camera } from '../shared/camera';
import { track } from '../shared/analytics';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

function esc(t: string): string {
  return t.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

const icon = (weapon: number, small = false) => `<img class="icon${small ? ' small' : ''}" src="${weaponIcon(weapon)}" alt="" />`;
const swatch = (mat: number) => `<span class="swatch" style="background:${cssColor(MATERIALS[mat].color)}"></span>`;

// --- 1. the goal, 2. a turn ---------------------------------------------------------------------------------------------

demoPanel(
  new Stage($<HTMLCanvasElement>('goal-stage')),
  [...$('goal-list').querySelectorAll<HTMLElement>('.pick')].map((el) => ({ el, scene: GOAL_SCENES[el.dataset.scene as keyof typeof GOAL_SCENES] })),
  $('goal-caption'),
);

const turnStage = new Stage($<HTMLCanvasElement>('turn-stage'));
const steps = [...$('turn-list').querySelectorAll<HTMLElement>('.pick')];
demoPanel(turnStage, steps.map((el, i) => ({ el, scene: TURN_SCENES[i] })), $('turn-caption'));
// The bar of the turn follows the step being played.
const bars = [...$('turn-timeline').querySelectorAll('span')];
const fillBars = () => {
  const at = steps.findIndex((s) => s.classList.contains('playing'));
  bars.forEach((b, i) => b.classList.toggle('on', i <= at));
};
new MutationObserver(fillBars).observe($('turn-list'), { subtree: true, attributes: true, attributeFilter: ['class'] });
fillBars();

// --- 3. synergies -----------------------------------------------------------------------------------------------------------

interface Synergy { id: string; a: number; b: number | string; name: string; text: string }

const SYNERGIES: Synergy[] = [
  { id: 'wave-icemine', a: WEAPON_WAVE, b: WEAPON_ICE_MINE, name: 'Ice wall', text: 'The wave freezes into a wall of ice over the mine: a rampart, or a dam.' },
  { id: 'wave-electric', a: WEAPON_WAVE, b: WEAPON_BATTERY, name: 'Electric wave', text: 'Over a Robotman’s carcass (or a battery), the discharge runs along the trail and strikes every invader the wave carries.' },
  { id: 'wave-cocktail', a: WEAPON_WAVE, b: WEAPON_COCKTAIL, name: 'Fire surge', text: 'A wave launched over burning oil catches fire and sets fire to everything it touches.' },
  { id: 'geyser-cryo', a: WEAPON_GEYSER, b: WEAPON_CRYO, name: 'Ice pillar', text: 'The column of the geyser freezes into a tower of ice that stays.' },
  { id: 'pistol-mine', a: WEAPON_PISTOL, b: WEAPON_MINE, name: 'Remote detonation', text: 'A bullet sets a mine off from afar, under the enemy who thought it was safe.' },
  { id: 'laser-ice', a: WEAPON_LASER, b: WEAPON_METEOR, name: 'Ricochet', text: 'Ice is a mirror for the ray: it comes back and hits the enemy behind the shooter.' },
  { id: 'remote-lava', a: WEAPON_REMOTE, b: 'lava', name: 'Lava drop', text: 'Lift a square of lava and drop it right above the enemy.' },
  { id: 'doll-geyser', a: WEAPON_DOLL, b: WEAPON_GEYSER, name: 'Catapult', text: 'The geyser throws the doll up, and the linked enemy flies with it.' },
  { id: 'bazooka-frozen', a: WEAPON_BAZOOKA, b: 'frozen', name: 'Shatter', text: 'An explosion frees a frozen invader, who takes ×1.5 damage.' },
  { id: 'acid-powder', a: WEAPON_POWDER_BARREL, b: WEAPON_ACID_BARREL, name: 'Acid bomb', text: 'Blow up a powder barrel next to an acid barrel: it bursts too, and sprays its acid all around.' },
  { id: 'powder-flamer', a: WEAPON_POWDER_BARREL, b: WEAPON_FLAMER, name: 'Fuse', text: 'The trail of the powder barrel is a fuse: light its far end, the fire runs along it and blows up the barrel, right next to the enemy.' },
  { id: 'pistol-acid', a: WEAPON_PISTOL, b: WEAPON_ACID_BARREL, name: 'Acid rain', text: 'A bullet from afar bursts the barrel: its drops of acid burn the enemy standing next to it.' },
  { id: 'volcano-powder', a: WEAPON_VOLCANO, b: WEAPON_POWDER_BARREL, name: 'Molten fuse', text: 'The lava of the volcano runs down its cone: when it reaches a powder barrel, it blows up.' },
  { id: 'volcano-wave', a: WEAPON_VOLCANO, b: WEAPON_WAVE, name: 'Quenched', text: 'The wave sets the lava of the volcano into obsidian: the flow stops, and the ground is walkable again.' },
  { id: 'roomba-wave', a: WEAPON_ROOMBA, b: WEAPON_WAVE, name: 'Wave eater', text: 'A wave that comes near a Roomba stops dead and is swallowed: it protects whoever stands behind it.' },
  { id: 'roomba-volcano', a: WEAPON_ROOMBA, b: WEAPON_VOLCANO, name: 'Lava drinker', text: 'A Roomba swallows the lava of the volcano as it runs, and fears nothing.' },
  { id: 'roomba-powder', a: WEAPON_ROOMBA, b: WEAPON_POWDER_BARREL, name: 'Defused', text: 'A Roomba eats the trail of a powder barrel: no more fuse to light.' },
];

function other(b: number | string): string {
  if (typeof b === 'number') return icon(b);
  if (b === 'lava') return `<span class="chip">${swatch(Physique.LAVA)}lava</span>`;
  return '<span class="chip">❄ frozen invader</span>';
}

$('synergy-rows').innerHTML = SYNERGIES.map((s) => `<tr class="pick" data-id="${s.id}">`
  + `<td><span class="combo"><span class="play-dot"></span>${icon(s.a)}<span class="plus">+</span>${other(s.b)}</span></td>`
  + `<td class="name"><b>${esc(s.name)}</b><small>${esc(WEAPONS[s.a].name)} + ${typeof s.b === 'number' ? esc(WEAPONS[s.b].name) : s.b === 'lava' ? 'Lava' : 'Frozen'}</small></td>`
  + `<td class="how">${esc(s.text)}</td></tr>`).join('');
const synergyRows = [...$('synergy-rows').querySelectorAll<HTMLElement>('tr')];
demoPanel(new Stage($<HTMLCanvasElement>('synergy-stage')), synergyRows.map((el) => ({ el, scene: SYNERGY_SCENES[el.dataset.id!] })));
nowPlaying(synergyRows, $('synergy-now'), (el) => SYNERGIES.find((s) => s.id === el.dataset.id)?.name ?? '');

const { WATER, LAVA, STEAM, OBSIDIAN, FIRE, WOOD, OIL, BLACK_POWDER, ACID, ROCK, SAND, ICE } = Physique;
const REACTIONS: [string, string][] = [
  [`${swatch(WATER)}water + ${swatch(LAVA)}lava`, `${swatch(OBSIDIAN)}obsidian + ${swatch(STEAM)}steam (obsidian takes 3 explosions)`],
  [`${swatch(FIRE)}fire + ${swatch(WATER)}water`, `${swatch(STEAM)}steam`],
  [`${swatch(STEAM)}steam, after a few seconds`, `${swatch(WATER)}rain`],
  [`${swatch(FIRE)}fire or ${swatch(LAVA)}lava + ${swatch(WOOD)}wood or ${swatch(OIL)}oil`, `${swatch(FIRE)}fire that runs on as long as there is wood or oil (oil floats on water)`],
  [`${swatch(FIRE)}fire or ${swatch(LAVA)}lava + ${swatch(BLACK_POWDER)}black powder`, 'an explosion (a big heap makes a big one)'],
  [`${swatch(ACID)}acid + ${swatch(ROCK)}rock, ${swatch(SAND)}sand, gravel, ${swatch(WOOD)}wood`, 'eaten away, and the acid wears out (obsidian resists)'],
  [`${swatch(ICE)}ice + ${swatch(LAVA)}lava`, `${swatch(OBSIDIAN)}obsidian + ${swatch(STEAM)}steam, with a burst of steam`],
  [`${swatch(FIRE)}fire + ${swatch(ICE)}ice`, `${swatch(WATER)}water (fast)`],
  ['any explosion', 'the crater vanishes, its water boils, what burns on its edge catches fire'],
];
$('reaction-rows').innerHTML = REACTIONS.map(([a, b]) => `<tr><td>${a}</td><td>${b}</td></tr>`).join('');

// --- 4. invaders -------------------------------------------------------------------------------------------------------------

/** Passive, death burst and legacy of each invader (legacy.ts, unit.ts, fire.ts). */
const LORE: Record<number, { passive: string; burst: string; legacy: string }> = {
  [ZORG]: { passive: 'Jumps twice as high, and glides: no fall damage.', burst: 'His saucer beams him away.', legacy: 'None.' },
  [GOLEM]: { passive: 'Walks 30 % slower.', burst: 'Breaks into blocks of rock.', legacy: 'A rock pillar: 4, 8, then 12 cells, a wall that stays.' },
  [SANDMAN]: { passive: 'Tough against explosions.', burst: 'Collapses into a mound of sand.', legacy: '120 cells of sand each turn.' },
  [FISHMAN]: { passive: 'Swims twice as fast, never drowns in water.', burst: 'Bursts into a wave of water.', legacy: 'A spring: 150 cells of water each turn.' },
  [LAVAMAN]: { passive: 'Swims in lava. Water covers him in obsidian.', burst: 'A geyser of lava.', legacy: '80 cells of lava each turn.' },
  [TREEMAN]: { passive: 'Water heals him.', burst: 'Sets into a wooden stump.', legacy: 'A tree grows: a bridge, or a cover that burns.' },
  [ROBOTMAN]: { passive: 'Sinks like a stone in liquids.', burst: 'Bursts into sparks: an electric discharge.', legacy: 'Discharges of radius 6, 8, then 10 (through the water).' },
  [CLOUDMAN]: { passive: 'Glides, never drowns.', burst: 'The jar breaks: a cloud of steam.', legacy: 'Steam clouds that turn into rain.' },
  [FIREMAN]: { passive: 'Lives in flames.', burst: 'A ball of fire that sets the invaders around on fire.', legacy: 'Fire, then embers thrown around, then a plume.' },
  [BARBE_NOIRE]: { passive: 'On fire or in lava, his beard blows up 2 s later (40 HP). Wet, he is safe.', burst: 'His powder blows up.', legacy: '40 cells of black powder each turn: a trap.' },
  [LIZARDMAN]: { passive: 'Leaves a drop of acid every 4 cells.', burst: 'Melts into a puddle of acid.', legacy: '60 cells of acid each turn: the ground gives way.' },
  [SNOWMAN]: { passive: 'Cold heals him.', burst: 'Locked in ice; the water around freezes.', legacy: 'Ice around his stele: the lake becomes a bridge.' },
};

function factor(label: string, pct: number): string {
  if (pct === 100) return '';
  if (pct === 0) return `<span class="chip good">${label} immune</span>`;
  const f = `×${(pct / 100).toString()}`;
  return `<span class="chip ${pct > 100 ? 'bad' : 'good'}">${label} ${f}</span>`;
}

function damageChips(k: InvaderKind): string {
  const chips = [factor('fire', k.fire), factor('lava', k.lava), factor('acid', k.acid), factor('explosions', k.explosion)].filter(Boolean);
  return chips.length ? `<div class="chips">${chips.join('')}</div>` : '<span class="muted">×1 everywhere</span>';
}

function liquidName(mat: number): string {
  return MATERIALS[mat].name.toLowerCase();
}

function elementChips(k: InvaderKind): string {
  const chips: string[] = [];
  if (k.water === 'hurt') chips.push(`<span class="chip bad">water −${WATER_HURT} HP/turn</span>`);
  if (k.water === 'heal') chips.push(`<span class="chip good">water +${WATER_HEAL} HP/turn</span>`);
  if (k.water === 'defused') chips.push('<span class="chip good">water defuses him</span>');
  if (k.freeze === 'immune') chips.push('<span class="chip good">never frozen</span>');
  if (k.freeze === 'healed') chips.push('<span class="chip good">cold heals</span>');
  if (k.noDrown.length === 4) chips.push('<span class="chip good">never drowns</span>');
  else if (k.noDrown.length) chips.push(`<span class="chip good">no drowning in ${k.noDrown.map(liquidName).join(', ')}</span>`);
  if (k.speed !== 100) chips.push(`<span class="chip bad">walk ${k.speed} %</span>`);
  if (k.swim !== 100) chips.push(`<span class="chip good">swim ×${k.swim / 100}</span>`);
  if (k.jump !== 100) chips.push(`<span class="chip good">jump ×${k.jump / 100}</span>`);
  if (k.glide) chips.push('<span class="chip good">glides</span>');
  if (k.heavy) chips.push('<span class="chip">sinks</span>');
  return chips.length ? `<div class="chips">${chips.join('')}</div>` : '<span class="muted">—</span>';
}

$('invader-rows').innerHTML = INVADERS.map((k, kind) => {
  const lore = LORE[kind];
  const matter = k.material >= 0 ? `${swatch(k.material)}${esc(MATERIALS[k.material].name)}` : kind === ROBOTMAN ? '⚡ Electricity' : '—';
  return `<tr class="pick" data-kind="${kind}">`
    + `<td class="name"><div class="who"><canvas class="sprite" width="60" height="60" data-kind="${kind}"></canvas><div><b><span class="play-dot"></span>${esc(k.name)}</b><small>${esc(k.title)}<br />${matter}</small></div></div></td>`
    + `<td>${damageChips(k)}</td><td>${elementChips(k)}<div class="how passive">${esc(lore.passive)}</div></td>`
    + `<td class="how"><b>Burst:</b> ${esc(lore.burst)}<br /><b>Legacy:</b> ${esc(lore.legacy)}</td></tr>`;
}).join('');
const invaderRows = [...$('invader-rows').querySelectorAll<HTMLElement>('tr')];
demoPanel(new Stage($<HTMLCanvasElement>('invader-stage')), invaderRows.map((el) => ({ el, scene: invaderScene(Number(el.dataset.kind)) })), $('invader-caption'));
nowPlaying(invaderRows, $('invader-now'), (el) => INVADERS[Number(el.dataset.kind)].name);

// Every sprite of the table plays its animations in turn: idle, walk, damage, death (GDD section 7).
const SEQUENCE = [0, 1, 2, 3, 0, 1, 2, 3, 4, 5, 6, 7, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 17, 17];
const spriteCam = { x: 0, y: 0, scale: 3 } as Camera;
const sprites = [...document.querySelectorAll<HTMLCanvasElement>('canvas.sprite')];
function animateSprites(now: number): void {
  const step = Math.floor(now / 140);
  sprites.forEach((c, i) => {
    const ctx = c.getContext('2d')!;
    ctx.clearRect(0, 0, c.width, c.height);
    drawFrame(ctx, spriteCam, Number(c.dataset.kind), SEQUENCE[(step + i * 5) % SEQUENCE.length], 2, 4, 1);
  });
  requestAnimationFrame(animateSprites);
}
requestAnimationFrame(animateSprites);

// --- 5. weapons ----------------------------------------------------------------------------------------------------------------

/** What each weapon does (weapons.ts, arsenal.ts, surge.ts, fire.ts, cold.ts, battery.ts). */
const DOES: Record<number, string> = {
  [WEAPON_BAZOOKA]: 'A shell carried by the wind. It explodes on impact and everything in its blast radius vanishes.',
  [WEAPON_GRENADE]: 'Thrown in an arc. It bounces, then explodes after 3 s. The wind barely moves it.',
  [WEAPON_PISTOL]: `${PISTOL_SHOTS} bullets per turn for one ammo. They fly straight with no range limit, chip the ground and set off mines. You keep moving between shots.`,
  [WEAPON_SANDBAG]: 'A big mound of sand: bury an enemy, build cover, plug a hole or a stream.',
  [WEAPON_DRILL]: 'Your invader bores a tunnel through anything, rock and obsidian included, for 3 s. It is a move, not your shot.',
  [WEAPON_MINE]: 'Armed from the next turn. It goes off when an invader comes close, or when lava or a bullet hits it.',
  [WEAPON_WAVE]: 'A wall of water rolls along the ground. It carries invaders, sand and mines and sets lava into obsidian. A wall higher than the wave stops it.',
  [WEAPON_FLAMER]: 'A jet of flames for 1.5 s. It burns wood, oil and powder, and every invader it licks takes damage and catches fire.',
  [WEAPON_COCKTAIL]: 'A bottle that spills a puddle of burning oil.',
  [WEAPON_CRYO]: 'Bursts after 3 s. Water turns to ice, lava to obsidian, and invaders freeze and skip their next turn.',
  [WEAPON_ICE_MINE]: 'Freezes everything around when it goes off. An invader that triggers it on its own turn stops right there.',
  [WEAPON_METEOR]: 'A block of ice falls straight onto the point you pick, explodes and lines its crater with ice.',
  [WEAPON_LASER]: 'A ray with no range limit. Ice reflects it like a mirror, it bounces off up to 5 walls and hits every invader it crosses.',
  [WEAPON_GEYSER]: 'Put it down: 5 s into the next player’s turn, a wide column of water and steam springs for 2 s and throws invaders, chests and shells into the air.',
  [WEAPON_DOLL]: 'Pick an enemy, then throw the doll: the enemy copies its flight, falls included.',
  [WEAPON_REMOTE]: 'Lift a 96 × 96 square of terrain anywhere and drop it anywhere, turned. Invaders stay behind and fall into the hole.',
  [WEAPON_JETPACK]: '6 s of flight. It comes off when the fuel runs out or when you land. It is a move, not your shot.',
  [WEAPON_ACID_BARREL]: 'Rolls from where it lands, down the slopes too, and leaves a big trail of acid that eats the ground. It stays 3 turns; hurt, it sprays acid all around.',
  [WEAPON_POWDER_BARREL]: 'Rolls from where it lands and leaves a thick trail of black powder: a fuse. It stays 3 turns; hurt, or touched by fire, it blows up.',
  [WEAPON_VOLCANO]: 'A little cone of obsidian rises on the ground. It erupts at the start of each of the next 3 turns, lava bombs and a pour of lava, then melts into lava itself.',
  [WEAPON_ROOMBA]: 'An invincible vacuum cleaner that slides about for 3 turns. When it sees sand, liquid or gas, a whirlwind opens in front of its mouth: it goes to and fro and swallows it all, a Wave too. Otherwise it goes straight on and falls into holes.',
  [WEAPON_BATTERY]: 'Stays where it lands and gives an electric discharge (through connected water too) at the start of each of the next 4 turns.',
};

function howTo(weapon: number): string {
  const w = WEAPONS[weapon];
  switch (w.mode) {
    case 'charge': return w.lob
      ? 'Aim, <b>hold</b> the click to charge the throw, release. The dotted line shows the start of the arc.'
      : 'Aim, <b>hold</b> the click to charge the power, release to fire.';
    case 'direct': return weapon === WEAPON_PISTOL ? 'Aim, <b>click</b> for each bullet.' : 'Aim, <b>click</b>: it goes off at once.';
    case 'hold': return '<b>Hold</b> the click: the jet follows the mouse.';
    case 'target': return '<b>Click</b> a point of the map.';
    case 'link': return '<b>Click</b> an enemy (framed in purple), then throw like a grenade.';
    case 'remote': return '<b>Click</b> the square to lift, move it, <b>wheel</b> to turn it, click to drop it. It is refused if it would land on an invader.';
    case 'jetpack': return '<b>Click</b> to put it on, then hold <kbd>Space</kbd> to rise and <kbd>←</kbd> <kbd>→</kbd> to steer.';
    case 'drill': return '<b>Hold</b> the click: your invader bores towards the mouse.';
  }
}

function ammo(weapon: number): string {
  const w = WEAPONS[weapon];
  if (!w.chest) return `<span class="chip base">base · ${w.ammo < 0 ? '∞' : `×${w.ammo}`}</span>`;
  const entry = CHEST_TABLE.find((e) => e.content === weapon);
  return `<span class="chip">chest · ${entry ? entry.weight : 0} %</span>`;
}

function damage(weapon: number): string {
  const w = WEAPONS[weapon];
  if (weapon === WEAPON_FLAMER) return '5 per hit<br /><small class="muted">45 max</small>';
  if (weapon === WEAPON_COCKTAIL) return 'fire';
  if (weapon === WEAPON_DOLL || weapon === WEAPON_GEYSER || weapon === WEAPON_REMOTE) return '<small class="muted">falls</small>';
  if (weapon === WEAPON_ACID_BARREL) return '20<br /><small class="muted">per drop</small>';
  if (weapon === WEAPON_VOLCANO) return '10<br /><small class="muted">per bomb</small>';
  if (w.damage === 0) return '—';
  return `${w.damage}${weapon === WEAPON_BATTERY ? ' ×4' : ''}`;
}

const order = [...BAR_ORDER, ...CHEST_TABLE.map((e) => e.content).filter((c) => c < WEAPONS.length)];
$('weapon-rows').innerHTML = order.map((weapon) => `<tr class="pick" data-weapon="${weapon}">`
  + `<td class="name"><div class="who">${icon(weapon)}<b><span class="play-dot"></span>${esc(WEAPONS[weapon].name)}</b></div></td>`
  + `<td>${ammo(weapon)}</td><td class="num">${damage(weapon)}</td><td class="how">${howTo(weapon)}</td><td class="how">${esc(DOES[weapon])}</td></tr>`).join('');
const weaponRows = [...$('weapon-rows').querySelectorAll<HTMLElement>('tr')];
demoPanel(new Stage($<HTMLCanvasElement>('weapon-stage')), weaponRows.map((el) => ({ el, scene: WEAPON_SCENES[Number(el.dataset.weapon)] })));
nowPlaying(weaponRows, $('weapon-now'), (el) => WEAPONS[Number(el.dataset.weapon)].name);

/** Under a stage: the name of what it plays, and how to pick another. */
function nowPlaying(rows: HTMLElement[], el: HTMLElement, name: (row: HTMLElement) => string): void {
  const update = () => {
    const row = rows.find((r) => r.classList.contains('playing'));
    el.innerHTML = row ? `Playing: <b>${esc(name(row))}</b> · click a row to watch it` : '';
  };
  new MutationObserver(update).observe(rows[0].parentElement!, { subtree: true, attributes: true, attributeFilter: ['class'] });
  update();
}

// The players' path (admin page): a visit that may begin here (How to play, from a search engine).
track('visit', { page: 'gameplay' });
